import React, { useCallback, useEffect } from "react";
import {
  createInitialSubnet,
  getDefaultAllocationPools,
  getDefaultCIDR,
  getDefaultGatewayIP,
  getDefaultNameservers,
} from "../helpers/helper";
import { Subnet } from "../../types/subnet";
import { useNetworkFormContext } from "../../context/NetworkFormContext";
import { safeToLowerCase } from "../../../../../../shared-functions/string";
import useFetchNameServers from "./useFetchDNS";
import { keysOf } from "../../../../../../shared-functions/objects";
import useZoneId from "../../../../../../custom-hooks/region/useZoneId";
import usePrevious from "../../../../../../custom-hooks/usePrevious";

const useSubnetsList = () => {
  const { formData, predefined_params, handleSubnetsChange, setSubnetRefs } =
    useNetworkFormContext();

  const zoneId = useZoneId(formData?.region || predefined_params?.region);

  const selectedRegion = safeToLowerCase(
    formData.region || predefined_params?.region,
  );

  const prevRegionRef = usePrevious(selectedRegion);

  const subnets = formData.subnets;
  const nameservers = useFetchNameServers();

  const onAddSubnet = useCallback(() => {
    const defaultNameservers = getDefaultNameservers({
      nameservers,
      ipVersion: 4,
      region: selectedRegion,
    });

    const newSubnet = createInitialSubnet(zoneId, defaultNameservers);
    const updatedSubnets = subnets ? [...subnets, newSubnet] : [newSubnet];
    handleSubnetsChange(updatedSubnets);

    setSubnetRefs((prev) => [
      ...(prev || []),
      React.createRef<HTMLDivElement>(),
    ]);
  }, [
    handleSubnetsChange,
    setSubnetRefs,
    nameservers,
    selectedRegion,
    subnets,
    zoneId,
  ]);

  const onDeleteSubnet = useCallback(
    (index: number) => {
      const updatedSubnets = subnets.filter((_, i) => i !== index);
      handleSubnetsChange(updatedSubnets);
      setSubnetRefs((prevRefs) => prevRefs.filter((_, i) => i !== index));
    },
    [handleSubnetsChange, setSubnetRefs, subnets],
  );

  const handleSingleSubnetChange = useCallback(
    (index: number, updatedSubnet: Subnet) => {
      const updated = [...subnets];
      updated[index] = updatedSubnet;
      handleSubnetsChange(updated);
    },
    [subnets, handleSubnetsChange],
  );

  // Create one subnet initially after nameservers are loaded
  useEffect(() => {
    if (!keysOf(nameservers).length || subnets?.length > 0) {
      return;
    } else {
      onAddSubnet();
    }
  }, [
    nameservers,
    selectedRegion,
    zoneId,
    subnets?.length,
    handleSubnetsChange,
    onAddSubnet,
  ]);

  // Update existing subnets values if region changes
  useEffect(() => {
    if (!keysOf(nameservers).length) return;
    if (!subnets?.length) return;

    if (selectedRegion === prevRegionRef) return;

    const updatedSubnets = subnets.map((subnet) => {
      const defaultNameservers = getDefaultNameservers({
        nameservers,
        ipVersion: subnet.ipVersion,
        region: selectedRegion,
      });
      return {
        ...subnet,
        cidr: getDefaultCIDR(zoneId),
        gatewayIp: getDefaultGatewayIP(zoneId),
        dnsNameservers: defaultNameservers,
        allocationPools: getDefaultAllocationPools(zoneId),
      };
    });

    handleSubnetsChange(updatedSubnets);
  }, [
    selectedRegion,
    nameservers,
    zoneId,
    subnets,
    handleSubnetsChange,
    prevRegionRef,
  ]);

  return {
    subnets,
    nameservers,
    onAddSubnet,
    handleSingleSubnetChange,
    onDeleteSubnet,
  };
};

export default useSubnetsList;
